import React, { useEffect, useRef } from 'react';
import ImagePlaceholder from './ImagePlaceholder';

/* ── 2. Clip-path reveal — bottom to top ──
   Phone shell has explicit min/max dimensions so threshold 0 is safe. */
function usePhoneReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.clipPath = 'inset(100% 0 0 0 round 40px)';
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.transition = 'clip-path 0.95s cubic-bezier(0.77, 0, 0.175, 1)';
        el.style.clipPath = 'inset(0% 0 0 0 round 40px)';
        observer.disconnect();
      }
    }, { threshold: 0, rootMargin: '0px 0px -60px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function PhoneFrame({ src, alt, imageName, badge, stats }) {
  const phoneRevealRef = usePhoneReveal();
  const phoneShell = (
    <div
      ref={phoneRevealRef}
      style={{
        width: 'min(240px, 62vw)',
        height: 'min(500px, calc(62vw * 500 / 240))',
        background: '#1B1A28',
        borderRadius: '40px',
        border: '3px solid rgba(63,196,227,0.35)',
        boxShadow: '0 40px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(63,196,227,0.1)',
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      {/* Volume buttons */}
      <div className="phone-vol-btn" style={{ top: '90px' }} />
      <div className="phone-vol-btn" style={{ top: '128px' }} />
      {/* Power button */}
      <div className="phone-power-btn" />
      {/* Screen */}
      <div style={{ position: 'absolute', inset: 0, borderRadius: '37px', overflow: 'hidden' }}>
        {src ? (
          <img src={src} alt={alt} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
        ) : (
          <ImagePlaceholder name={imageName || alt} height="100%" />
        )}
      </div>
    </div>
  );

  if (!src) {
    return (
      <div className="relative flex justify-center">
        <div className="absolute rounded-full blur-3xl" style={{ width: '200px', height: '200px', background: 'rgba(63,196,227,0.15)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 0 }} />
        <div style={{ position: 'relative', zIndex: 1 }}>{phoneShell}</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '32px 24px', paddingBottom: stats ? '52px' : '32px' }}>
      {/* Dark gradient background */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(145deg, #0f1923 0%, #0b2d3d 60%, #06111a 100%)',
        borderRadius: '28px',
        overflow: 'hidden',
      }}>
        {/* Decorative glow blobs */}
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(254,92,115,0.12)', filter: 'blur(40px)' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(63,196,227,0.1)', filter: 'blur(30px)' }} />
        {/* Subtle dot grid */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
      </div>

      {/* Phone with subtle perspective tilt */}
      <div style={{ transform: 'perspective(900px) rotateY(-6deg) rotateX(2deg)', position: 'relative', zIndex: 2, filter: 'drop-shadow(0 32px 48px rgba(0,0,0,0.5))' }}>
        {phoneShell}
      </div>

      {/* Floating badge — top right */}
      {badge && (
        <div style={{
          position: 'absolute', top: '20px', right: '12px',
          background: '#ffffff',
          borderRadius: '12px',
          padding: '10px 14px',
          boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          display: 'flex', alignItems: 'center', gap: '8px',
          zIndex: 10,
        }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#FE5C73', flexShrink: 0, boxShadow: '0 0 6px rgba(254,92,115,0.6)', display: 'inline-block' }} />
          <span style={{ fontSize: '11px', fontFamily: 'popinsMedium', color: '#1B1A28', whiteSpace: 'nowrap' }}>{badge}</span>
        </div>
      )}

      {/* Floating stat card — bottom left */}
      {stats && (
        <div style={{
          position: 'absolute', bottom: '12px', left: '12px',
          background: '#ffffff',
          borderRadius: '16px',
          padding: '14px 18px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          minWidth: '160px',
          zIndex: 10,
        }}>
          <div style={{ fontSize: '10px', color: '#9CA3AF', fontFamily: 'popinsMedium', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
            {stats.label}
          </div>
          <div style={{ fontSize: '22px', fontFamily: 'popinsBold', color: '#1B1A28', lineHeight: 1 }}>
            {stats.value}
          </div>
          {stats.sub && (
            <div style={{ fontSize: '10px', color: '#3FC4E3', fontFamily: 'popinsMedium', marginTop: '5px' }}>
              ↑ {stats.sub}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
